import axios, { AxiosHeaders, AxiosInstance } from 'axios';
import keycloak from '~/shared/guards/Keycloak';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
if (!API_BASE_URL) {
  throw new Error('API_BASE_URL is not defined in environment variables');
}

type getDefaultHeadersArgs = {
  hasData: boolean;
};

// default header for all requests
const getDefaultHeaders = ({ hasData }: getDefaultHeadersArgs): Record<string, string> => {
  const headers: Record<string, string> = {};
  if (hasData) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
};

function setAuthHeader(config: any, token: string) {
  if (!config.headers) {
    config.headers = {};
  }

  // Axios may provide plain object headers or AxiosHeaders.
  if (config.headers instanceof AxiosHeaders) {
    config.headers.set('Authorization', `Bearer ${token}`);
  } else {
    config.headers = {
      ...(config.headers as Record<string, unknown>),
      Authorization: `Bearer ${token}`,
    };
  }
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: getDefaultHeaders({ hasData: false }),
  timeout: 30000,
});

// Public client for endpoints that must NOT receive Authorization header.
// This intentionally does not attach Keycloak interceptors.
export const publicApiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: getDefaultHeaders({ hasData: false }),
  timeout: 30000,
});

// Simple client-side rate limiting (queues bursts safely).
// Default: 8 requests per second (adjust via VITE_API_RATE_LIMIT_RPS).
const RATE_LIMIT_RPS = Number(import.meta.env.VITE_API_RATE_LIMIT_RPS ?? 8);
const RATE_WINDOW_MS = 1000;
let requestTimestamps: number[] = [];

async function rateLimit(): Promise<void> {
  if (!Number.isFinite(RATE_LIMIT_RPS) || RATE_LIMIT_RPS <= 0) return;

  const now = Date.now();
  requestTimestamps = requestTimestamps.filter((t) => now - t < RATE_WINDOW_MS);

  if (requestTimestamps.length < RATE_LIMIT_RPS) {
    requestTimestamps.push(now);
    return;
  }

  const earliest = requestTimestamps[0] ?? now;
  const waitMs = Math.max(0, RATE_WINDOW_MS - (now - earliest));
  await new Promise((resolve) => setTimeout(resolve, waitMs));

  const after = Date.now();
  requestTimestamps = requestTimestamps.filter((t) => after - t < RATE_WINDOW_MS);
  requestTimestamps.push(after);
}

// Request interceptor to add Authorization header
apiClient.interceptors.request.use(
  async (config) => {
    await rateLimit();

    if (keycloak.authenticated) {
      try {
        // Ensures the access token is valid; refreshes using refresh token if needed.
        await keycloak.updateToken(30);
      } catch (error) {
        console.error('Token refresh failed', error);
        keycloak.logout();
        return config;
      }

      if (keycloak.token) {
        setAuthHeader(config, keycloak.token);
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Apply the same rate limit to public endpoints too.
publicApiClient.interceptors.request.use(
  async (config) => {
    await rateLimit();
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry && keycloak.authenticated) {
      originalRequest._retry = true;

      try {
        // Try refreshing token via Keycloak
        await keycloak.updateToken(30);

        if (keycloak.token) {
          setAuthHeader(originalRequest, keycloak.token);
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        console.error('Keycloak refresh failed', refreshError);
        keycloak.logout();
      }
    }

    return Promise.reject(error);
  }
);
